import cookie from "js-cookie";
import { redirectWithCurrentPage } from "lib/login";

export class APIError extends Error {
    constructor(message, response, json) {
        super(message);
        this.response = response;
        this.json = json;
        this.name = "APIError";
    }
}

function checkStatus(response) {
    // A 302 from a dashboard API endpoint to /dashboard/login is followed by
    // the browser and surfaces here as a 200 OK with response.redirected=true
    // and the login page's URL. We must check this *before* the ok branch,
    // otherwise we'd return the login HTML and JSON parsing would fail later.
    if (
        response.redirected === true &&
        new URL(response.url).pathname.endsWith("/dashboard/login")
    ) {
        const error = new APIError("Dashboard session expired", response);
        error.name = "DashboardAuthExpiredError";
        error.isDashboardAuthExpired = true;
        throw error;
    }

    if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.split(";")[0] === "application/json") {
            return response.json();
        }

        return response.text();
    }

    if (response.status === 401 && response.headers.get("Location")) {
        // if there's an ajax error of the special kind from ApiHandler (python)
        // we want the user to login and come back to the current page
        redirectWithCurrentPage(response.headers.get("Location"));
    }

    // If we have an error from api.py, it should be a json object with an 'error'
    // key, but in case, let's catch any json decode errors and just pass back
    // the status text as the error.
    return response
        .json()
        .catch(() => {
            throw new APIError(response.statusText, response);
        })
        .then((json) => {
            throw new APIError(json.error || response.statusText, response, json);
        });
}

// function generateFormData(body) {
//     const form = new FormData();
//
//     Object.keys(body).forEach((k) => {
//         const v = body[k];
//         if (Array.isArray(v)) {
//             for (let i = 0; i < v.length; i += 1) {
//                 form.append(`${k}[]`, v[i]);
//             }
//         } else {
//             form.append(k, body[k]);
//         }
//     });
//
//     return form;
// }

const runFetch = (url, options = {}) =>
    fetch(url, {
        ...options,
        credentials: "same-origin",
    }).then(checkStatus);

export default runFetch;

// function genericPostBodyMethod(url, method, body = {}, options = {}) {
//     const form = generateFormData(body);
//
//     return runFetch(url, {
//         method,
//         body: form,
//         ...options,
//     });
// }

export function get(url, params = {}, options = {}) {
    const query = Object.keys(params).map(
        (k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`,
    );
    const q = query.length > 0 ? "?" : "";

    return runFetch(`${url}${q}${query.join("&")}`, {
        method: "GET",
        ...options,
    });
}

export function sendJSON(url, method, body, { headers, ...options }) {
    return runFetch(url, {
        method,
        headers: {
            "Content-Type": "application/json",
            ...headers,
        },
        body: JSON.stringify(body),
        ...options,
    });
}

export function post(url, body = {}, options = {}) {
    return sendJSON(url, "POST", body, options);
}

export function patch(url, body = {}, options = {}) {
    const replacePatch = cookie.get("__wspatch");
    let method = "PATCH";

    /**
     * Forces PATCH calls to send as POSTs to avoid old firewalls
     */
    if (replacePatch) {
        method = "POST";
        // eslint-disable-next-line
        options.headers = {
            ...options.headers,
            "WS-Method": "PATCH",
        };
    }

    return sendJSON(url, method, body, options);
}

export function del(url, options = {}) {
    return runFetch(url, {
        method: "DELETE",
        ...options,
    });
}
